import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { ClipboardPaste, Loader2, Wand2, Check } from "lucide-react";
import { API } from "../App";
import { TranscriptEditor } from "./TranscriptEditor";

const POLL_INTERVAL = 3000;

const TranscriptPaster = ({ sessionType, patientId, transcript, onTranscriptChange, onFieldsGenerated }) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const chars = transcript.trim().length;

  const pollForResult = async (generationId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      setProgress(Math.min((i + 1), 95));
      try {
        const res = await axios.get(`${API}/session-reports/generation/${generationId}`, { withCredentials: true });
        if (res.data.status === "completed") {
          onFieldsGenerated(res.data.fields, res.data.suggested_exercises || [], transcript);
          toast.success(`Compte rendu généré à partir de la transcription${res.data.route?.label ? ` · ${res.data.route.label}` : ""}`);
          return;
        }
        if (res.data.status === "error") throw new Error(res.data.error || "Erreur de génération");
      } catch (err) {
        if (err.response?.status === 404) continue;
        throw err;
      }
    }
    throw new Error("Délai d'attente dépassé");
  };

  const generate = async () => {
    setProcessing(true);
    setProgress(2);
    try {
      const res = await axios.post(`${API}/session-reports/generate-from-text`,
        { text: transcript, session_type: sessionType, patient_id: patientId || null },
        { withCredentials: true });
      await pollForResult(res.data.generation_id);
    } catch (err) {
      toast.error(`Erreur de génération : ${err.response?.data?.detail || err.message}`);
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  return (
    <Card className="border-dashed border-2 border-primary/20" data-testid="transcript-paster">
      <CardContent className="pt-5 pb-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ClipboardPaste className="w-4 h-4" />
          <span>Collez la transcription de votre enregistreur (Plaud, Otter…) — elle sera conservée dans le compte rendu.</span>
        </div>
        <TranscriptEditor value={transcript} onChange={onTranscriptChange} disabled={processing} testId="paste-transcript" />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-muted-foreground" data-testid="paste-transcript-count">
            {chars > 0 ? <><Check className="w-3 h-3 inline mr-1 text-primary" />{chars.toLocaleString("fr-FR")} caractères — enregistrée avec le CR</> : "Aucune transcription collée"}
          </span>
          <Button onClick={generate} disabled={processing || chars < 50} data-testid="generate-from-transcript-btn">
            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {processing ? `Génération… ${progress}%` : "Générer le compte rendu avec l'IA"}
          </Button>
        </div>
        {processing && (
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TranscriptPaster;
